(() => {
  'use strict';

  const MAX_FILES = 4;
  const MAX_SIDE = 2000;
  const TARGET_BYTES = 1_200_000;
  const FINAL_BYTES = 2_000_000;
  const REQUEST_TIMEOUT_MS = 85_000;
  const TURNSTILE_WAIT_MS = 12_000;

  function publishEvent(name, data = {}) {
    try {
      const publish = window.Shopify?.analytics?.publish;
      if (typeof publish !== 'function') return;
      const result = publish(`ez_hair:${name}`, data);
      if (typeof result?.catch === 'function') result.catch(() => {});
    } catch {
      // Analytics must never block a consultation submission.
    }
  }

  function setStatus(form, message, state = '') {
    const status = form.querySelector('[data-consultation-status]');
    if (!status) return;
    status.textContent = message;
    if (state) status.dataset.state = state;
    else delete status.dataset.state;
  }

  function setTurnstileStatus(message, state) {
    document.querySelectorAll('[data-consultation-form]').forEach((form) => {
      if (state) form.dataset.turnstileState = state;
      setStatus(form, message, 'error');
    });
  }

  function turnstileErrorMessage(errorCode) {
    const code = String(errorCode ?? '');
    if (code.startsWith('110')) return 'Bezpečnostná kontrola nie je povolená pre túto stránku. Obnovte stránku a skúste znova.';
    if (code.startsWith('200')) return 'Bezpečnostnú kontrolu sa nepodarilo načítať. Obnovte stránku alebo skúste iný prehliadač.';
    if (code.startsWith('300') || code.startsWith('600')) return 'Bezpečnostná kontrola zlyhala. Obnovte stránku a skúste znova.';
    return 'Bezpečnostnú kontrolu sa nepodarilo dokončiť. Obnovte stránku a skúste znova.';
  }

  window.ezHairConsultationTurnstileSuccess = () => {
    document.querySelectorAll('[data-consultation-form]').forEach((form) => {
      delete form.dataset.turnstileState;
    });
  };
  window.ezHairConsultationTurnstileError = (errorCode) => {
    setTurnstileStatus(turnstileErrorMessage(errorCode), 'error');
    return false;
  };
  window.ezHairConsultationTurnstileExpired = () => {
    setTurnstileStatus('Bezpečnostná kontrola vypršala. Obnovte ju a skúste znova.', 'expired');
  };
  window.ezHairConsultationTurnstileTimeout = () => {
    setTurnstileStatus('Bezpečnostná kontrola trvá príliš dlho. Skúste to znova.', 'timeout');
  };
  window.ezHairConsultationTurnstileUnsupported = () => {
    setTurnstileStatus('Tento prehliadač nevie dokončiť bezpečnostnú kontrolu. Skúste aktuálny Chrome, Safari alebo Firefox.', 'unsupported');
  };

  function secureRandomUuid() {
    const cryptoApi = window.crypto;
    if (typeof cryptoApi?.randomUUID === 'function') return cryptoApi.randomUUID();
    if (typeof cryptoApi?.getRandomValues !== 'function') throw new Error('secure-random-unavailable');

    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  function idempotencyKey(form) {
    if (!form.dataset.idempotencyKey) form.dataset.idempotencyKey = secureRandomUuid();
    return form.dataset.idempotencyKey;
  }

  function loadBitmap(file) {
    if (window.createImageBitmap) return window.createImageBitmap(file);
    return new Promise((resolve, reject) => {
      const image = new Image();
      const url = URL.createObjectURL(file);
      image.onload = () => {
        URL.revokeObjectURL(url);
        resolve(image);
      };
      image.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('image-load-failed'));
      };
      image.src = url;
    });
  }

  function canvasBlob(canvas, quality) {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('image-encode-failed'))), 'image/jpeg', quality);
    });
  }

  async function preparePhoto(file, index) {
    const bitmap = await loadBitmap(file);
    const width = bitmap.width || bitmap.naturalWidth;
    const height = bitmap.height || bitmap.naturalHeight;
    if (!width || !height || width * height > 20_000_000) throw new Error('image-too-large');

    const scale = Math.min(1, MAX_SIDE / Math.max(width, height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) throw new Error('canvas-unavailable');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    if (typeof bitmap.close === 'function') bitmap.close();

    let blob;
    for (const quality of [0.85, 0.75, 0.65]) {
      blob = await canvasBlob(canvas, quality);
      if (blob.size <= TARGET_BYTES) break;
    }
    if (!blob || blob.size > FINAL_BYTES) throw new Error('image-output-too-large');
    return new File([blob], `photo-${index + 1}.jpg`, { type: 'image/jpeg' });
  }

  function photoInputs(form) {
    return Array.from(form.querySelectorAll('[data-consultation-files]'));
  }

  function selectedPhotoFiles(form) {
    return photoInputs(form)
      .map((input) => input.files?.[0])
      .filter(Boolean);
  }

  function releasePhotoPreview(input) {
    const slot = input.closest('[data-photo-slot]');
    const preview = slot?.querySelector('[data-photo-preview]');
    if (preview?.dataset.objectUrl) URL.revokeObjectURL(preview.dataset.objectUrl);
    if (preview) {
      preview.removeAttribute('src');
      preview.hidden = true;
      delete preview.dataset.objectUrl;
    }
  }

  function updatePhotoSlot(input) {
    const slot = input.closest('[data-photo-slot]');
    if (!slot) return;
    const file = input.files?.[0];
    const preview = slot.querySelector('[data-photo-preview]');
    const state = slot.querySelector('[data-photo-state]');
    const remove = slot.querySelector('[data-photo-remove]');
    releasePhotoPreview(input);

    if (!file) {
      slot.classList.remove('is-filled');
      if (state) state.textContent = state.dataset.emptyText || 'Odfotiť alebo vybrať';
      if (remove) remove.hidden = true;
      return;
    }

    if (preview) {
      const objectUrl = URL.createObjectURL(file);
      preview.src = objectUrl;
      preview.dataset.objectUrl = objectUrl;
      preview.hidden = false;
    }
    slot.classList.add('is-filled');
    if (state) state.textContent = 'Pridaná · ťuknite pre zmenu';
    if (remove) remove.hidden = false;
  }

  function updatePhotoProgress(form) {
    const count = selectedPhotoFiles(form).length;
    const counter = form.querySelector('[data-consultation-photo-count]');
    if (counter) {
      counter.textContent = `${count} / ${MAX_FILES}`;
      counter.setAttribute('aria-label', `${count} zo ${MAX_FILES} fotografií pridaných`);
    }
    return count;
  }

  function resetPhotoUi(form) {
    for (const input of photoInputs(form)) updatePhotoSlot(input);
    updatePhotoProgress(form);
  }

  async function waitForTurnstileToken(form) {
    const deadline = Date.now() + TURNSTILE_WAIT_MS;
    while (Date.now() < deadline) {
      const token = form.querySelector('input[name="turnstileToken"]')?.value?.trim();
      if (token) return token;
      await new Promise((resolve) => window.setTimeout(resolve, 250));
    }
    throw new Error('turnstile-unavailable');
  }

  function resetTurnstile(form) {
    const widget = form.querySelector('.cf-turnstile');
    if (!widget || typeof window.turnstile?.reset !== 'function') return;
    try {
      window.turnstile.reset(widget);
    } catch {
      // The next page load will create a fresh challenge if reset is unavailable.
    }
  }

  function executeTurnstile(form) {
    const widget = form.querySelector('.cf-turnstile');
    if (!widget || typeof window.turnstile?.execute !== 'function') return;
    try {
      window.turnstile.execute(widget);
    } catch {
      throw new Error('turnstile-unavailable');
    }
  }

  async function submit(form) {
    const files = selectedPhotoFiles(form);
    const endpoint = form.dataset.endpoint;
    if (photoInputs(form).length < 1 || !endpoint) throw new Error('form-not-configured');
    if (files.length < 1 || files.length > MAX_FILES) throw new Error('wrong-photo-count');

    const preparedPhotos = [];
    for (const [index, file] of files.entries()) {
      preparedPhotos.push(await preparePhoto(file, index));
    }

    executeTurnstile(form);
    const turnstileToken = await waitForTurnstileToken(form);
    const payload = new FormData(form);
    payload.set('turnstileToken', turnstileToken);
    payload.delete('photos');
    for (const photo of preparedPhotos) payload.append('photos', photo);

    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timeout = controller ? window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS) : null;
    let response;
    try {
      response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKey(form) },
        body: payload,
        credentials: 'omit',
        ...(controller ? { signal: controller.signal } : {}),
      });
    } catch (error) {
      if (controller?.signal.aborted) throw new Error('request-timeout');
      throw error;
    } finally {
      if (timeout !== null) window.clearTimeout(timeout);
    }
    if (!response.ok) {
      if (response.status === 403) resetTurnstile(form);
      const error = new Error('submit-failed');
      error.status = response.status;
      throw error;
    }
    form.dataset.idempotencyKey = '';
  }

  function errorMessage(error) {
    switch (error?.message) {
      case 'wrong-photo-count':
        return 'Vyberte 1 až 4 fotografie vlasov.';
      case 'image-too-large':
      case 'image-output-too-large':
        return 'Jedna z fotografií je príliš veľká. Skúste menší súbor alebo inú fotografiu.';
      case 'image-load-failed':
        return 'Fotografie musia byť platné JPG alebo PNG súbory.';
      case 'secure-random-unavailable':
        return 'Tento prehliadač nepodporuje bezpečné odoslanie formulára.';
      case 'request-timeout':
        return 'Odosielanie trvá príliš dlho. Skontrolujte pripojenie a skúste znova.';
      case 'turnstile-unavailable':
        return 'Bezpečnostnú kontrolu sa nepodarilo načítať. Obnovte stránku a skúste znova.';
      default:
        break;
    }

    if (error?.status === 403) return 'Bezpečnostná kontrola zlyhala. Obnovte stránku a skúste znova.';
    if (error?.status === 413) return 'Fotografie sú príliš veľké. Vyberte menšie súbory a skúste znova.';
    if (error?.status === 429) return 'Formulár bol odoslaný príliš často. Skúste znova o chvíľu.';
    if (error?.status === 503) return 'Konzultáciu teraz nevieme doručiť. Skúste znova o chvíľu.';
    return 'Konzultáciu sa nepodarilo odoslať. Skontrolujte údaje a skúste znova.';
  }

  function validatePhotoCount(form) {
    const count = updatePhotoProgress(form);
    return count >= 1 && count <= MAX_FILES;
  }

  function setup(form) {
    const inputs = photoInputs(form);
    for (const input of inputs) {
      const slot = input.closest('[data-photo-slot]');
      const state = slot?.querySelector('[data-photo-state]');
      const remove = slot?.querySelector('[data-photo-remove]');
      if (state) state.dataset.emptyText = state.textContent.trim();
      input.addEventListener('change', () => {
        updatePhotoSlot(input);
        const count = updatePhotoProgress(form);
        publishEvent('photo_added', { photo_count: count });
        if (count > 0) setStatus(form, '');
      });
      remove?.addEventListener('click', () => {
        input.value = '';
        updatePhotoSlot(input);
        updatePhotoProgress(form);
      });
    }
    resetPhotoUi(form);
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!validatePhotoCount(form)) {
        setStatus(form, 'Pridajte aspoň jednu fotografiu vlasov.', 'error');
        inputs[0]?.focus();
        return;
      }
      if (!form.reportValidity()) return;
      const submitButton = form.querySelector('button[type="submit"]');
      if (submitButton) submitButton.disabled = true;
      form.setAttribute('aria-busy', 'true');
      setStatus(form, 'Fotografie pripravujeme a bezpečne odosielame…', 'pending');
      try {
        await submit(form);
        form.reset();
        resetPhotoUi(form);
        resetTurnstile(form);
        publishEvent('consultation_submit_success');
        setStatus(form, 'Ďakujeme. Fotografie sme prijali a ozveme sa vám.', 'success');
      } catch (error) {
        setStatus(form, errorMessage(error), 'error');
      } finally {
        if (submitButton) submitButton.disabled = false;
        form.setAttribute('aria-busy', 'false');
      }
    });
  }

  document.querySelectorAll('[data-consultation-form]').forEach((form) => {
    setup(form);
    publishEvent('consultation_open');
  });
})();
